import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Store, Coins, Loader2, ShoppingCart } from "lucide-react";
import AdBanner from "@/components/AdBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ShopsDirectory() {
  const [shops, setShops] = useState(null);
  useEffect(() => { axios.get(`${API}/shops`).then(({ data }) => setShops(data)).catch(() => setShops([])); }, []);
  if (!shops) return null;
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10" data-testid="shops-directory-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/" data-testid="shops-back-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-lime-400 transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Pickle Bullpen
        </Link>
        <p className="font-mono text-lime-300 uppercase tracking-[0.3em] text-xs mb-4">The Bullpen Marketplace</p>
        <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-3">Merchant Shops</h1>
        <p className="text-zinc-400 max-w-2xl mb-12">
          Browse verified Bullpen merchants and pay with BULLION COIN — gear, services, exclusive cards and more,
          all inside the ecosystem.
        </p>
        <AdBanner zone="shops" />
        {shops.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-14 text-center text-zinc-600 uppercase tracking-widest text-sm" data-testid="shops-empty">
            No shops are open yet — approved merchants will appear here.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shops-grid">
            {shops.map((s) => (
              <Link key={s.id} to={`/shops/${s.id}`} data-testid={`shop-card-${s.id}`}
                className="block border border-lime-400/30 bg-white/[0.03] p-5 transition-transform duration-200 hover:-translate-y-1">
                <Store className="w-6 h-6 text-lime-400 mb-3" />
                <h3 className="font-display font-bold uppercase text-lg leading-tight mb-1">{s.business_name}</h3>
                <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500 mb-2">
                  {s.type === "card_merchant" ? "Card Merchant" : "Merchant"} · {s.products} item{s.products === 1 ? "" : "s"}
                </div>
                {s.description && <p className="text-xs text-zinc-400 line-clamp-2">{s.description}</p>}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Storefront() {
  const { id } = useParams();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API}/shops/${id}`).then(({ data }) => {
    setShop(data);
    document.title = `${data.business_name} — Bullpen Merchant Shop | Pickle Bullpen`;
  }).catch(() => setMissing(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const buy = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/shop/products/${p.id}/buy`);
      toast.success(`Order placed — "${p.name}" for ${p.price_blc} BLC. The merchant has been notified!`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (missing) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4" data-testid="shop-not-found">
      <p className="uppercase tracking-widest text-zinc-500">Shop not found</p>
      <Link to="/shops" className="text-lime-400 text-sm underline">Browse all shops</Link>
    </div>
  );
  if (!shop) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10" data-testid="storefront-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/shops" data-testid="storefront-back-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-lime-400 transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> All Shops
        </Link>
        <div className="flex items-center gap-2 mb-2">
          <Store className="w-5 h-5 text-lime-400" />
          <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
            Verified {shop.type === "card_merchant" ? "Card Merchant" : "Merchant"}
          </span>
        </div>
        <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl mb-3" data-testid="storefront-title">{shop.business_name}</h1>
        {shop.description && <p className="text-zinc-400 max-w-2xl mb-10">{shop.description}</p>}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="storefront-products">
          {shop.products.length === 0 && (
            <div className="col-span-full border border-dashed border-zinc-800 p-10 text-center text-zinc-600 uppercase tracking-widest text-sm">
              Nothing on the shelves yet.
            </div>
          )}
          {shop.products.map((p) => (
            <div key={p.id} className="border border-zinc-800 bg-white/[0.03] flex flex-col" data-testid={`product-${p.id}`}>
              {p.image && <img src={p.image} alt={p.name} className="w-full aspect-square object-cover" />}
              <div className="p-4 flex flex-col gap-2 flex-1">
                <div className="font-display font-bold uppercase leading-tight">{p.name}</div>
                {p.description && <p className="text-xs text-zinc-500 line-clamp-2">{p.description}</p>}
                <div className="flex items-center justify-between mt-auto pt-2">
                  <span className="flex items-center gap-1 font-mono font-black text-lime-300">
                    <Coins className="w-3.5 h-3.5" /> {p.price_blc} BLC
                  </span>
                  <span className="text-[9px] uppercase tracking-widest text-zinc-600">{p.stock > 0 ? `${p.stock} left` : "Sold out"}</span>
                </div>
                {user ? (
                  <button data-testid={`buy-${p.id}`} onClick={() => buy(p)} disabled={!!busy || p.stock < 1}
                    className="w-full flex items-center justify-center gap-2 bg-lime-400 text-black font-black uppercase tracking-widest py-2.5 text-[10px] hover:bg-lime-300 active:scale-95 transition-all disabled:opacity-40">
                    {busy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />}
                    {p.stock > 0 ? "Buy with Coins" : "Sold Out"}
                  </button>
                ) : (
                  <Link to="/auth" data-testid={`login-to-buy-${p.id}`}
                    className="w-full text-center border border-lime-400/50 text-lime-300 font-black uppercase tracking-widest py-2.5 text-[10px] hover:bg-lime-400/10 transition-colors">
                    Sign in to buy
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
